import { AnyAction } from '@reduxjs/toolkit';
import { IconsEnum } from 'components/Icons';
import { AppFragment } from 'enums/path-fragment';
import { signOut } from 'features/auth/authActions';
import { setSession, setToken } from 'features/auth/authSlice';
import { AuthTokenType } from 'features/auth/types';
import { setAlertMessage, setWalletExpired } from 'features/dashboard/dashboardSlice';
import { DASHBOARD_EXPIRATION, DASHBOARD_SESION_KEY, DashBoardState } from 'features/dashboard/types';
import { setIsOnline } from 'features/location/locationSlice';
import { SessionState } from 'hooks';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { TokenUtilsType } from 'lib/auth/web';
import { useAppDispatch } from 'lib/centralStore';
import Persist, { StorageKind } from 'lib/persist';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef } from 'react';
import { EventsType, useIdleTimer, workerTimers } from 'react-idle-timer';
import { CONNECTED_KEY_NAME, isClientSide, REFRESH_KEY_NAME } from 'utility/constant';
import { utcNow } from 'utility/date';
import { getSessionId, hasValue, isFrame, isMatch, isPersonalUrl, isSeamless, isTruthy } from 'utility/functions';
import { mutex } from '.';

const DELAY = 14999;
const cookie = Persist(StorageKind.cookie);
const session = Persist(StorageKind.session);
const isFrameDocument = isFrame();
const isSeamlessDocument = isSeamless();
const isStandAlone = isFrameDocument || isSeamlessDocument;

interface PingType {
  ts: number;
  code?: number;
  refresh?: boolean;
}

const getToken = async (): Promise<TokenUtilsType> => {
  const response = await fetch('/api/auth/token');

  if (response.ok) {
    return response.json();
  }

  return Promise.reject();
};

const doPing = () =>
  new Promise<PingType>(async (resolve) => {
    const ts = utcNow().getTime();
    let result: PingType = { ts };
    try {
      const response = await fetch(`/api/ping?v=${Date.now()}`, { method: 'POST' });
      result.code = response.status;
      if (response.ok) {
        const { pong: ts, refresh } = await response.json();
        if (isTruthy(ts)) {
          result.ts = ts;
        }
        if (isTruthy(refresh)) {
          result.refresh = true;
        }
      } else {
        console.error(`Status:${response.status}, StatusText:${response.statusText}`);
      }
    } catch (e) {
      result.code = 500;
      // return Promise.reject(e);
    } finally {
      resolve(result);
    }
  });

const setDriver = () => {
  const me = getSessionId();
  cookie.setItem(REFRESH_KEY_NAME, me, DELAY);
};

const events: Array<EventsType> = [
  'wheel',
  'focus',
  'keydown',
  'mousemove',
  'mousedown',
  'touchmove',
  'touchstart',
  'mousewheel',
  'MSPointerDown',
  'MSPointerMove',
  'DOMMouseScroll',
];

export const RefreshTokenEngine = () => {
  const dispatch = useAppDispatch();
  const { asPath, push } = useRouter();
  const { data, status, update } = useSession();

  const traceRef = useRef(false);
  const resetRef = useRef<Function | undefined>();
  const statusRef = useRef('');
  const messageRef = useRef<Function | undefined>();
  const releaseRef = useRef<Function | undefined>();
  const isActivating = useRef(false);

  const genericErrorDescription = useGetLabelByKey('description-generic-error');

  const isDriver = () => {
    let owner = cookie.getItem(REFRESH_KEY_NAME);
    // push current
    if (!hasValue(owner)) {
      setDriver();
      return true;
    }

    const me = getSessionId();
    return owner === me;
    // save the clean unique list
  };

  const doLock = useCallback(async (payload: boolean) => {
    if (isTruthy(payload)) {
      // console.log('RefreshTokenEngine.mutex:acquire...?')
      releaseRef.current = await mutex.acquire();
      if (traceRef.current) {
        console.log('RefreshTokenEngine.mutex:acquire: ok');
      }
    } else if (typeof releaseRef.current === 'function') {
      // console.log('RefreshTokenEngine.mutex:release...?')
      releaseRef.current();
      if (traceRef.current) {
        console.log('RefreshTokenEngine.mutex:release: ok');
      }
    } else {
      if (traceRef.current) {
        console.log('RefreshTokenEngine.mutex:nulla da rilasciare');
      }
    }
  }, []);

  const doLogout = useCallback(() => {
    if (isStandAlone || ![`${SessionState.authenticated}`].includes(status)) return;

    try {
      dispatch(signOut({ isInactive: false, dtNow: utcNow() }));
    } finally {
      dispatch(
        setAlertMessage({
          icon: IconsEnum.SUCCESS,
          color: 'var(--color-success)',
          message: genericErrorDescription,
          animation: false,
        })
      );
    }

    if (isPersonalUrl(asPath)) {
      push(`/${AppFragment.Sport}`);
    }
  }, [status, asPath, genericErrorDescription, push, dispatch]);

  const doSessionUpdate = useCallback(
    async (payload) => {
      // console.log(`RefreshTokenEngine.session... ${status}`);
      // console.log('RefreshTokenEngine.session...?')
      // TODO : se nel multitab conviene sparare setSession da sola perchè altrimenti manda in tilt le sessioni
      dispatch(setSession({ ...payload }));
      // updateSession({ ...payload });
      if (traceRef.current) {
        console.log('RefreshTokenEngine.session: ok');
      }
    },
    [dispatch]
  );

  const doTokenUpdate = useCallback(
    async (payload: AuthTokenType) => {
      // console.log('RefreshTokenEngine.token...?')
      await dispatch(setToken(payload));
      if (traceRef.current) {
        console.log('RefreshTokenEngine.token: ok');
      }
    },
    [dispatch]
  );

  const doNotifyConnected = useCallback(
    async (payload: boolean) => {
      // console.log('RefreshTokenEngine.online...?')
      await dispatch(setIsOnline(payload));
      if (traceRef.current) {
        console.log('RefreshTokenEngine.online: ok');
      }
    },
    [dispatch]
  );

  const onIdle = useCallback(async () => {
    if (isClientSide) {
      const search = new URLSearchParams(document.location.search.substring(1));
      const trace = search.get('trace') ?? '';
      traceRef.current = trace.toLowerCase().includes('token');
    }

    // console.log(`RefreshTokenEngine.status:${status}, data:${JSON.stringify(data)}`);

    // some one else is owner of refreshing the token
    if (isDriver()) {
      if (traceRef.current) {
        console.log(`RefreshTokenEngine.provider`);
      }
    } else {
      if (traceRef.current) {
        console.log(`RefreshTokenEngine.consumer`);
      }
      return;
    }

    try {
      if ([`${SessionState.loading}`].includes(status)) {
        if (traceRef.current) {
          console.log('RefreshTokenEngine.session-loading - skip');
        }
      } else if (mutex.isLocked()) {
        if (traceRef.current) {
          console.log('RefreshTokenEngine.executing - wait');
        }
        await mutex.waitForUnlock();
      }

      const { code, refresh } = await doPing();

      const isAuthenticated = status === SessionState.authenticated;

      const doRefresh = isAuthenticated ? update : getToken;

      if (typeof messageRef.current === 'function') {
        // messageRef.current({ type: 'lock', payload: true }, true);
        if (code === 403) {
          messageRef.current({ type: 'logout', payload: false }, true);
        } else if (refresh) {
          messageRef.current({ type: 'lock', payload: true }, true);

          const nextSession = await doRefresh();

          messageRef.current({ type: 'session', payload: { ...nextSession, isAuthenticated } }, true);

          // messageRef.current({ type: 'token', payload: token }, true);
          messageRef.current({ type: 'lock', payload: false }, true);
        }
        messageRef.current({ type: 'online', payload: code !== 500 }, true);
        // messageRef.current({ type: 'lock', payload: false }, true);
      } else {
        if (code === 403) {
          doLogout();
        } else if (refresh) {
          doLock(true);

          const nextSession = await doRefresh();

          doSessionUpdate({ ...nextSession, isAuthenticated });

          doLock(false);
        }
        doNotifyConnected(code !== 500);
      }

      if (isAuthenticated) {
        let dashboard = {} as DashBoardState;
        try {
          dashboard = JSON.parse(session.getItem(DASHBOARD_SESION_KEY) ?? '{}');
        } catch {
          dashboard = {} as DashBoardState;
        } finally {
          const { walletUpdated } = dashboard ?? {};
          const lastUpdate = walletUpdated ? new Date(walletUpdated).getTime() : 0;
          const elapsed = new Date().getTime() - lastUpdate;

          /// 300000 = 5 minutes
          if (elapsed >= DASHBOARD_EXPIRATION) {
            dispatch(setWalletExpired());
          }
        }
      }
    } finally {
      if (typeof resetRef.current === 'function') {
        resetRef.current();
      }
    }
  }, [status, update, doNotifyConnected, doLogout, doLock, doSessionUpdate, dispatch]);

  const onMessage = async ({ type, payload }: AnyAction) => {
    // console.log(`RefreshTokenEngine.message (type:${type}, payload:${JSON.stringify(payload)})`)
    switch (type) {
      case 'lock': {
        doLock(payload);
        break;
      }
      case 'token': {
        doTokenUpdate(payload);
        break;
      }
      case 'logout': {
        doLogout();
        break;
      }
      case 'online': {
        doNotifyConnected(payload);
        break;
      }
      case 'session': {
        doSessionUpdate(payload);
        break;
      }
    }
  };

  const { reset, message } = useIdleTimer({
    name: CONNECTED_KEY_NAME,
    events: [],
    timers: workerTimers,
    timeout: DELAY,
    crossTab: true,
    disabled: isStandAlone,
    syncTimers: 500,
    startOnMount: true,
    leaderElection: true,
    promptBeforeIdle: 0,
    onIdle,
    onMessage,
  });

  const doUnlist = () => {
    // write updated list
    cookie.removeItem(REFRESH_KEY_NAME);
  };

  useEffect(() => {
    resetRef.current = reset;
    messageRef.current = message;
  });

  const onActive = useCallback(() => {
    // keep timer's ownership and reset expiration

    if (isActivating.current) {
      return;
    }

    isActivating.current = true;
    if (!isDriver()) {
      setDriver();
      onIdle();
    }

    if (typeof resetRef.current === 'function') {
      resetRef.current();
    }

    isActivating.current = false;
  }, [onIdle]);

  useEffect(() => {
    if (isStandAlone) return;

    window.addEventListener('beforeunload', doUnlist, { passive: true });
    events.forEach(function (ev) {
      document.addEventListener(ev, onActive, { passive: true });
    });
    return () => {
      window.removeEventListener('beforeunload', doUnlist);
      events.forEach(function (ev) {
        document.removeEventListener(ev, onActive);
      });
    };
  }, [onActive]);

  useEffect(() => {
    if (isStandAlone) return;

    try {
      if (![`${SessionState.loading}`].includes(status)) {
        if (isMatch(Reflect.get(data ?? {}, 'error'), '^RefreshTokenError$')) {
          doLogout();
        } else if (status !== statusRef.current) {
          if (typeof messageRef.current === 'function') {
            // console.log(`RefreshTokenEngine.isAuthenticated: ${status === 'authenticated'} vs ${Object.hasOwn(data ?? {}, 'refreshToken')}`);
            messageRef.current({ type: 'session', payload: data }, false);
          }
          doSessionUpdate(data);
        }
      }
    } finally {
      statusRef.current = status;
      if (typeof resetRef.current === 'function') {
        resetRef.current();
      }
    }
  }, [data, status, onIdle, dispatch, doSessionUpdate, doLogout]);

  return <React.Fragment key={CONNECTED_KEY_NAME} />;
};
